import { api } from "./api";

/** Normalise FastAPI error shapes so React never tries to render an object.
 *
 * FastAPI `HTTPException(detail=...)` can take many shapes:
 *   • string                → "Chưa đăng nhập"
 *   • list[{loc, msg, ...}] → 422 validation errors
 *   • dict {msg}            → custom dict with `msg` key
 *   • dict {message}        → custom dict with `message` key (Phase 83y.18 invite duplicates)
 *   • dict {code, details}  → fully structured errors with sub-conflict list
 *   • anything else         → fallback to a generic Vietnamese message.
 */
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Có lỗi xảy ra, vui lòng thử lại.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : (typeof e === "string" ? e : JSON.stringify(e))))
      .filter(Boolean)
      .join(" · ");
  if (typeof detail === "object") {
    // Phase 83z.21 — Backend has 2 conventions: legacy `{msg}` and modern
    // `{code, message, details}`. Try `message` first since it's the more
    // canonical "human-readable" field, then `msg`, then `error`.
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.error === "string") return detail.error;
    if (typeof detail.detail === "string") return detail.detail;
    // Last resort: stringify so the user at least sees the raw payload
    // instead of "[object Object]".
    try {
      return JSON.stringify(detail);
    } catch {
      return "Có lỗi xảy ra, vui lòng thử lại.";
    }
  }
  return String(detail);
}

export const authApi = {
  login: (username, password) =>
    api.post("/auth/login", { username, password }).then((r) => r.data),
  register: (payload) => api.post("/auth/register", payload).then((r) => r.data),
  // Phase 85.A.185 — Gộp verify-OTP + register vào 1 request (UX bớt 1 step).
  registerWithOtp: (payload) =>
    api.post("/auth/register-with-otp", payload).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  me: (opts) => api.get("/auth/me", opts?.noRefresh ? { __noRefresh: true } : undefined).then((r) => r.data),
  refresh: () => api.post("/auth/refresh").then((r) => r.data),
  forgotPassword: (email) =>
    api.post("/auth/forgot-password", { email }).then((r) => r.data),
  // v16.44 — Zalo phone-based forgot password flow.
  forgotPasswordZaloRequestOtp: (phone) =>
    api.post("/auth/forgot-password/zalo/request-otp", { phone }).then((r) => r.data),
  forgotPasswordZaloVerifyOtp: (phone, otpCode) =>
    api
      .post("/auth/forgot-password/zalo/verify-otp", { phone, otp_code: otpCode })
      .then((r) => r.data),
  resetPassword: (token, newPassword) =>
    api
      .post("/auth/reset-password", { token, new_password: newPassword })
      .then((r) => r.data),
  // Phase 81aaa — self-service profile + password
  updateMe: (payload) => api.patch("/auth/me", payload).then((r) => r.data),
  changeMyPassword: (currentPassword, newPassword) =>
    api
      .post("/auth/me/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      })
      .then((r) => r.data),
  // Phase 85.A.68 — Tạo password lần đầu (cho user Google OAuth chưa có
  // local password). Không cần `current_password`.
  createMyPassword: (newPassword) =>
    api
      .post("/auth/me/create-password", { new_password: newPassword })
      .then((r) => r.data),
};
